import { Button, DialogActions, MenuItem } from "@material-ui/core";
import moment from "moment";
import React, { Component } from "reactn";
import styled from "styled-components";
import { ASSET_RESOLUTION } from "common/constants";
import { ActionPermissionsObject } from "common/interfaces";
import { Dropdown } from "js/Components/Dropdown";
import { TextField } from "js/Components/TextField";
import { ToggleSwitch } from "js/Components/ToggleSwitch";
import { WithLabel } from "js/Components/WithLabel";
import WorkspaceController from "js/controllers/WorkspaceController";
import getLogger, { LogGroup } from "js/core/logger";
import { ds } from "js/core/models/dataService";
import getUserProfile from "js/core/services/userProfiles";
import { generateTeamAssetMetadata } from "js/core/utilities/generateTeamAssetMetadata";
import Avatar from "js/react/components/Avatar";
import { BeautifulDialog, ShowConfirmationDialog, ShowDialog, ShowErrorDialog } from "js/react/components/Dialogs/BaseDialog";
import FetchingClickShield from "js/react/components/FetchingClickShield";
import { FlexSpacer, Gap10, Gap20, Gap30 } from "js/react/components/Gap";
import { FlexBox, ScrollBox } from "js/react/components/LayoutGrid";
import TagsList from "js/react/components/TagsList";
import {
    BlueButton,
    Section,
    UIPane,
    UIPaneContents,
    UIPaneHeader,
} from "js/react/components/UiComponents";
import Tooltip from "js/react/views/PPTAddin/Components/Tooltip";
import AddTagsDialog from "js/react/components/Dialogs/AddTagsDialog";
import { t } from "js/i18n";
import { ToolTipBadge } from "../../../../Components/ToolTip";
import { Divider } from "../../../../Components/Divider";

const logger = getLogger(LogGroup.ASSETS);

const AssetPreviewContainer = styled.div`
    width: 640px;
    height: 360px;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: #E0E0E0;
    background-repeat: no-repeat;
    background-position: center;
    background-size: contain;

    .replace-button {
        opacity: 0;
    }

    &:hover {
        .replace-button {
            opacity: 1;
        }
    }
`;

const UserInfo = styled.div`
    display: flex;
    align-items: center;
    width: 100%;

    .avatar {
        margin-right: 10px;
    }

    .name {
        font-size: 14px;
        font-weight: 600;
        color: #333;
    }

    .date {
        margin-left: 20px;
        font-size: 14px;
        color: #666;
    }
`;

class TeamAssetSettingsDialogComponent extends Component {
    constructor(props) {
        super(props);

        this.state = {
            type: "image",
            assetName: "",
            alt: "",
            hidden: false,
            url: "",
            tags: [],
            createdBy: "",
            modifiedBy: "",
            isGeneratingAltText: false,
            fetching: true
        };
    }

    componentDidMount() {
        this.loadStateFromAsset(this.props.assetResource);
    }

    async loadStateFromAsset(assetResource) {
        try {
            const asset = await ds.assets.getAssetById(assetResource.resourceId);
            const url = await asset.getURL(ASSET_RESOLUTION.SMALL);

            const createdBy = await getUserProfile(assetResource.metadata.createdBy);
            const modifiedBy = await getUserProfile(assetResource.metadata.modifiedBy);
            this.setState({
                type: assetResource.metadata.type || "image",
                assetName: assetResource.metadata.name,
                alt: assetResource.metadata.alt || assetResource.metadata.description || "",
                hidden: !assetResource.metadata.isPublished,
                tags: assetResource.tags ?? [],
                url,
                createdBy,
                modifiedBy,
                fetching: false
            });
        } catch (err) {
            logger.error(err, "[TeamAssetSettingsDialog] Error loading state from asset", { assetResource });
            ShowErrorDialog({
                title: t("Error loading asset"),
                message: t("An error occurred while loading the asset. Please try again.")
            });
        }
    }

    handleRemoveTag = tag => {
        let { tags } = this.state;

        if (tags) {
            tags = [...tags];
            const index = tags.indexOf(tag);
            if (index > -1) {
                tags.splice(index, 1);
                this.setState({ tags });
            }
        }
    }

    handleAddTag = () => {
        const { existingTags } = this.props;
        const { tags } = this.state;

        ShowDialog(AddTagsDialog, {
            activeTags: [...tags],
            existingTags,
            acceptCallback: newTags => {
                this.setState({
                    tags: newTags,
                });
            },
        });
    }

    handleSelectType = async value => {
        this.setState({
            type: value,
        });

        return true;
    }

    handleSaveChanges = async () => {
        const { assetResource, closeDialog } = this.props;
        const { type, assetName, hidden, tags, alt } = this.state;

        try {
            this.setState({ fetching: true });

            await WorkspaceController.updateWorkspaceResource({
                resourceId: assetResource.resourceId,
                resourceType: assetResource.resourceType,
                tags,
                metadata: {
                    type,
                    name: assetName,
                    alt: alt,
                    isPublished: !hidden
                }
            });

            closeDialog();
        } catch (err) {
            logger.error(err, "[TeamAssetSettingsDialog] Error saving changes", { assetResource });
            ShowErrorDialog({
                title: t("Error saving changes"),
                message: t("An error occurred while saving changes. Please try again.")
            });
        }
    }

    handleGenerateMetadata = async () => {
        const { assetResource } = this.props;

        await generateTeamAssetMetadata({
            resourceId: assetResource?.resourceId,
            onReportState: ({ name, altText, tags, isGenerating, error }) => {
                if (error) {
                    ShowErrorDialog({
                        title: t("Error generating metadata"),
                        message: t("An error occurred while generating the metadata. Please try again.")
                    });
                }

                this.setState(prevState => ({
                    isGeneratingAltText: !!isGenerating,
                    assetName: name?.trim() ? name : prevState.assetName,
                    alt: altText ?? prevState.alt,
                    tags: tags?.length ? tags : prevState.tags
                }));
            }
        });
    }

    render() {
        const { assetResource, closeDialog } = this.props;
        const { type, assetName, alt, hidden, createdBy, modifiedBy, url, tags, fetching, isGeneratingAltText } = this.state;
        const canGenerateMetadata = WorkspaceController.actionPermissions?.[ActionPermissionsObject.GENERATIVE_AI]?.use;

        return (
            <BeautifulDialog maxWidth="lg" closeDialog={closeDialog} closeButton>
                <FetchingClickShield visible={fetching} backgroundColor={"white"} />
                <UIPane style={{ overflow: "hidden" }}>
                    <UIPaneHeader>{t("Team Asset Settings")}</UIPaneHeader>
                    <ScrollBox>
                        <UIPaneContents style={{ minHeight: 200 }}>
                            <Section>
                                <Gap10 />
                                <FlexBox top left>
                                    <AssetPreviewContainer
                                        title={alt}
                                        style={{ backgroundImage: `url(${url})` }}
                                    >
                                    </AssetPreviewContainer>
                                    <Gap30 />
                                    <FlexBox column top left width={500}>
                                        <WithLabel label={t("Name")} above left fillWidth>
                                            <TextField
                                                autoFocus
                                                id="library-name"
                                                fullWidth
                                                variant="outlined"
                                                value={assetName}
                                                onChange={value => this.setState({ assetName: value })}
                                                onBlur={value => this.setState({ assetName: value })}
                                            />
                                        </WithLabel>
                                        <Gap20 />
                                        <WithLabel label={t("Description")} above left fillWidth>
                                            <TextField
                                                id="alt-text"
                                                multiline
                                                variant="outlined"
                                                fullWidth
                                                value={alt}
                                                onChange={value => this.setState({ alt: value })}
                                                onBlur={value => this.setState({ alt: value })}
                                            />
                                        </WithLabel>
                                        <Gap20 />
                                        <WithLabel label={t("Asset Type")} left>
                                            <Dropdown
                                                value={type}
                                                onChange={this.handleSelectType}
                                                overrideStyledSelect={{ width: "100%" }}
                                            >
                                                <MenuItem value="image">{t("Image")}</MenuItem>
                                                <MenuItem value="logo">{t("Logo")}</MenuItem>
                                            </Dropdown>
                                        </WithLabel>
                                        <Gap20 />
                                        <Divider />
                                        <Gap20 />
                                        <WithLabel label={t("Created by")} left fillWidth labelWidth={80}>
                                            <UserInfo>
                                                <Avatar url={createdBy.photoURL} />
                                                <div className="name">
                                                    <span className="fullName">
                                                        {
                                                            createdBy.displayName ||
                                                            createdBy.email ||
                                                            <Tooltip title={t("This data predates our tracking of asset changes.")} arrow>
                                                                <b>{t("Unknown")}</b>
                                                            </Tooltip>
                                                        }
                                                    </span>
                                                </div>
                                                <div className="date">{moment.utc(assetResource.createdAt).local().format("LL")}</div>
                                            </UserInfo>
                                        </WithLabel>
                                        <Gap20 />
                                        {modifiedBy &&
                                            <WithLabel label={t("Modified by")} left fillWidth labelWidth={80}>
                                                <UserInfo>
                                                    <Avatar url={modifiedBy.photoURL} />
                                                    <div className="name">
                                                        <span className="fullName">
                                                            {
                                                                modifiedBy.displayName ||
                                                                modifiedBy.email ||
                                                                <Tooltip title={t("This data predates our tracking of asset changes.")} arrow>
                                                                    <b>{t("Unknown")}</b>
                                                                </Tooltip>
                                                            }
                                                        </span>
                                                    </div>
                                                    <div className="date">{moment.utc(assetResource.modifiedAt).local().format("LL")}</div>
                                                </UserInfo>
                                            </WithLabel>
                                        }
                                        <Gap20 />
                                        <WithLabel
                                            label={t("Published")}
                                            left
                                            tight
                                        >
                                            <ToggleSwitch
                                                value={!hidden}
                                                onChange={async value => {
                                                    if (value) {
                                                        const confirmed = await ShowConfirmationDialog({
                                                            title: t("Are you sure you want to publish this asset?"),
                                                            message: t("Publishing this asset allows it to be used by your team."),
                                                            okButtonLabel: t("Publish Asset"),
                                                        });
                                                        if (!confirmed) {
                                                            return;
                                                        }
                                                    }
                                                    this.setState({ hidden: !value });
                                                }}
                                            />
                                            <ToolTipBadge>
                                                {t("Assets will be available for your team to use.")}
                                            </ToolTipBadge>
                                        </WithLabel>
                                    </FlexBox>
                                </FlexBox>
                            </Section>
                            <Section>
                                <FlexBox left middle fillWidth>
                                    <div className="section-title">{t("Tags")}</div>
                                    <Gap20 />
                                    <TagsList
                                        tags={tags}
                                        onRemove={this.handleRemoveTag}
                                        onClick={this.handleAddTag}
                                    />
                                </FlexBox>
                            </Section>
                        </UIPaneContents>
                    </ScrollBox>
                </UIPane>
                <DialogActions>
                    {canGenerateMetadata && (
                        <Button
                            onClick={this.handleGenerateMetadata}
                            color="primary"
                            disabled={isGeneratingAltText || !url}
                        >
                            {isGeneratingAltText ? t("Generating...") : t("Generate Metadata")}
                        </Button>
                    )}
                    <FlexSpacer />
                    <Button onClick={closeDialog}>{t("Cancel")}</Button>
                    <BlueButton onClick={this.handleSaveChanges}>{t("Save Changes")}</BlueButton>
                </DialogActions>
            </BeautifulDialog>
        );
    }
}

export default TeamAssetSettingsDialogComponent;
